import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Layout, Waves, Lock, Repeat2, Database, Globe, Server,
  ShieldAlert, Rocket, Star, ArrowUpRight, ArrowLeft, CheckCircle2,
  Megaphone, FileSpreadsheet, Landmark, Code2, Users,
} from "lucide-react";
import PageHero from "../components/PageHero";
import {
  CRM_IMAGE,
  ALL_SOLUTION_SLUGS,
  FX_SOLUTION_CROSS_LINKS,
  getFxSolutionBySlug,
  navItemPath,
} from "../lib/constants";

const ICON_MAP = {
  Sparkles, Layout, Waves, Lock, Repeat2, Database, Globe, Server,
  ShieldAlert, Rocket, UserStar: Star, Megaphone, FileSpreadsheet, Landmark, Code2, Users,
};

export default function FxProductPage() {
  const { slug } = useParams();
  const solution = getFxSolutionBySlug(slug);
  if (!solution) return <Navigate to="/" replace />;

  const Icon = ICON_MAP[solution.icon] || Sparkles;
  const idx = ALL_SOLUTION_SLUGS.indexOf(slug);
  const nextSlug = ALL_SOLUTION_SLUGS[(idx + 1) % ALL_SOLUTION_SLUGS.length];
  const prevSlug = ALL_SOLUTION_SLUGS[(idx - 1 + ALL_SOLUTION_SLUGS.length) % ALL_SOLUTION_SLUGS.length];
  const next = getFxSolutionBySlug(nextSlug);
  const prev = getFxSolutionBySlug(prevSlug);

  return (
    <>
      <PageHero
        eyebrow={`${solution.parentSection} · ${solution.group}`}
        breadcrumb={[
          { label: solution.parentSection, path: solution.parentSectionHref },
          { label: solution.title },
        ]}
        title={solution.intro}
        lede={solution.paragraphs[0]}
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative rounded-3xl overflow-hidden border border-white/10"
            >
              <img src={CRM_IMAGE} alt={solution.title} className="w-full h-auto object-cover" />
              <div className="absolute top-4 left-4 glass-panel rounded-full px-3 py-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em]">
                <span className="w-1.5 h-1.5 bg-[#00C2FF] rounded-full pulse-cyan" />
                {solution.title}
              </div>
            </motion.div>
          </div>

          <div className="md:col-span-5">
            <div className="w-14 h-14 rounded-2xl bg-[#00C2FF]/15 border border-[#00C2FF]/40 flex items-center justify-center text-[#00C2FF] mb-6">
              <Icon className="w-6 h-6" />
            </div>

            {solution.paragraphs.map((p, i) => (
              <p key={i} className="text-sm text-gray-300 leading-relaxed mb-4">
                {p}
              </p>
            ))}

            <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] mb-4 mt-6">
              / Highlights
            </div>
            <ul className="space-y-3">
              {solution.highlights.map((f, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#00C2FF] shrink-0 mt-1" />
                  <span className="text-sm text-gray-200">{f}</span>
                </motion.li>
              ))}
            </ul>

            <Link
              to="/contact"
              data-testid="product-cta-contact"
              className="btn-cyan inline-flex items-center gap-2 mt-10 px-7 py-3.5 rounded-full font-semibold"
            >
              Talk to a specialist <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#00C2FF] mb-6">
            / Explore more
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FX_SOLUTION_CROSS_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href.startsWith("/#") ? navItemPath({ href: link.href }) : link.href}
                className="p-4 rounded-xl border border-white/10 hover:border-[#00C2FF]/40 transition-all"
              >
                <div className="font-semibold text-white text-sm">{link.label}</div>
                <div className="text-xs text-gray-500 mt-1">{link.blurb}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 gap-4">
          <Link
            to={`/solutions/${prevSlug}`}
            data-testid="product-prev"
            className="group p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-all flex items-center gap-4"
          >
            <ArrowLeft className="w-5 h-5 text-[#00C2FF] group-hover:-translate-x-1 transition-transform" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Previous</div>
              <div className="font-display text-base font-semibold mt-1">{prev?.title}</div>
            </div>
          </Link>
          <Link
            to={`/solutions/${nextSlug}`}
            data-testid="product-next"
            className="group p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-all flex items-center gap-4 sm:justify-end sm:text-right"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Next</div>
              <div className="font-display text-base font-semibold mt-1">{next?.title}</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#00C2FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>
    </>
  );
}
